import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Alert,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useState } from "react";

const PreferencesStep = ({
  profileData,
  handleInputChange,
  initialWorkPreferenceState,
  currentWorkAuth,
  setCurrentWorkAuth,
  initialWorkAuthState,
  handleAddItem,
  handleDeleteItem,
  showNotification,
  DynamicListSection,
  preferredLocationOptions,
  workArrangementOptions,
  validationErrors,
}) => {
  const theme = useTheme(); // CHANGED: Instantiate theme
  const [currentPreference, setCurrentPreference] = useState({
    location: "",
    arrangement: "",
  });

  const domainOptions = ["AI Engineer", "Data Science", "Business Analytics"];

  // CHANGED: Removed local style objects (cardStyle, titleStyle, etc.)

  const cardStyle = {
    p: { xs: 2, md: 3 },
    borderRadius: "24px",
    bgcolor: "background.paper",
    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
  };


  const titleStyle = {
    fontWeight: "600",
    mb: 2,
    color: "primary.main", // CHANGED
  };

  const handleCurrentPreferenceChange = (event) => {
    const { name, value } = event.target;
    setCurrentPreference((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddWorkPreference = () => {
    if (profileData.work_preferences?.length >= 3) {
      if (showNotification) {
        showNotification(
          "You can add a maximum of 3 work preferences.",
          "warning"
        );
      }
      return;
    }
    if (!currentPreference.location || !currentPreference.arrangement) {
      if (showNotification) {
        showNotification(
          "Please select both a location and an arrangement.",
          "error"
        );
      }
      return;
    }
    handleAddItem("work_preferences", currentPreference);
    setCurrentPreference({ location: "", arrangement: "" });
  };

  const handleDomainInterestChange = (domain, isChecked) => {
    const currentInterests = profileData.domain_interest || [];
    let newInterests;

    if (isChecked) {
      newInterests = [...currentInterests, domain];
    } else {
      newInterests = currentInterests.filter((interest) => interest !== domain);
    }
    handleInputChange("domain_interest", newInterests);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
        Set your work preferences for better job matching.
      </Typography>

      {validationErrors.domain_interest && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.domain_interest}
        </Alert>
      )}
      {validationErrors.work_preferences && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.work_preferences}
        </Alert>
      )}
      {validationErrors.work_authorizations && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.work_authorizations}
        </Alert>
      )}

      <Stack spacing={4}>
        <Paper sx={cardStyle}>
          <Typography variant="h6" sx={titleStyle}>
            Domain Interests{" "}
            <span style={{ color: theme.palette.error.main }}>*</span>
          </Typography>
          <Stack>
            {domainOptions.map((domain) => (
              <FormControlLabel
                key={domain}
                control={
                  <Checkbox
                    checked={(profileData.domain_interest || []).includes(
                      domain
                    )}
                    onChange={(e) =>
                      handleDomainInterestChange(domain, e.target.checked)
                    }
                    color="primary" // CHANGED
                  />
                }
                label={domain}
              />
            ))}
          </Stack>
        </Paper>

        <Paper sx={cardStyle}>
          <Typography variant="h6" sx={titleStyle}>
            Work Preferences{" "}
            <span style={{ color: theme.palette.error.main }}>*</span>
          </Typography>
          <Stack spacing={2}>
            <Stack spacing={2}>
              <TextField
                select
                name="location"
                label="Location"
                value={currentPreference.location}
                onChange={handleCurrentPreferenceChange}
                displayEmpty
                // CHANGED: sx prop removed, styles handled by theme
              >
                <MenuItem value="" disabled>
                  <Typography color="text.secondary">Location</Typography>
                </MenuItem>
                {preferredLocationOptions?.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                name="arrangement"
                label="Work Arrangement"
                value={currentPreference.arrangement}
                onChange={handleCurrentPreferenceChange}
                displayEmpty
                // CHANGED: sx prop removed, styles handled by theme
              >
                <MenuItem value="" disabled>
                  <Typography color="text.secondary">
                    Work Arrangement
                  </Typography>
                </MenuItem>
                {workArrangementOptions?.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddWorkPreference}
                disabled={profileData.work_preferences?.length >= 3}
                color="primary"
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  px: 3,
                  py: 1.2,
                  fontWeight: "bold",
                  boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                Add Work Preference
              </Button>

            </Box>
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {profileData.work_preferences?.map((pref, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderRadius: "16px",
                    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
                  }}
                >

                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {pref.location}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pref.arrangement}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => handleDeleteItem("work_preferences", index)}
                    aria-label="delete preference"
                    size="small"
                    color="error" // CHANGED
                  >
                    <DeleteIcon />
                  </IconButton>
                </Paper>
              ))}
            </Stack>
          </Stack>
        </Paper>

        <Box>
          <DynamicListSection
            // The sx props here will override the default Paper styles in DynamicListSection
            sx={cardStyle}
            title="Work Authorizations *"
            field="work_authorizations"
            items={profileData.work_authorizations}
            formState={currentWorkAuth}
            setFormState={setCurrentWorkAuth}
            initialState={initialWorkAuthState}
            formFields={[
              {
                name: "type",
                label: "Authorization Type (e.g., H1B, Citizen)",
              },
              {
                name: "country",
                label: "Applicable Country",
              },
              {
                name: "valid_till",
                label: "Valid Till (Optional)",
              },
            ]}
            renderItem={(item) => (
              <Box>
                <Typography variant="subtitle2" color="primary.main">
                  {item.type} for {item.country}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.valid_till
                    ? `Valid until: ${item.valid_till}`
                    : "No expiry date"}
                </Typography>
              </Box>
            )}
            handleAddItem={handleAddItem}
            handleDeleteItem={handleDeleteItem}
            showNotification={showNotification}
          />
        </Box>

        <Paper sx={cardStyle}>
          <Typography variant="h6" sx={titleStyle}>
            Additional Preferences
          </Typography>
          <TextField
            multiline
            rows={4}
            label="Describe other preferences" // CHANGED
            value={profileData.generated_preferences || ""}
            onChange={(e) =>
              handleInputChange("generated_preferences", e.target.value)
            }
          />
        </Paper>
      </Stack>
    </Box>
  );
};

export default PreferencesStep;
